'use client'

import { useMemo, useState } from 'react'
import { Flex } from '@chakra-ui/react'
import { getValidTranslation, ProductCategory } from '@leid/api-products'
import { usePathname, useRouter } from 'next/navigation'

import { trackClickCategoryOnHomepage } from 'app/components/shared/Analytics/segment'
import { getCategoryName } from 'components/nav/CategoriesMenu/utils/categories'
import theme from 'styles/theme'

import CategoryCard from './CategoryCard'

type Props = {
  categories: ProductCategory[]
}

export type UICategory = {
  id: number
  name: string
  imageUrl?: string
  size: 'Small' | 'Large'
  description: string
  backgroundColor?: string
}

const getProductCategoryDescriptionAndColourBasedOnCategoryName = (
  categoryName: string,
): { description: string; color?: string; size: 'Small' | 'Large' } => {
  switch (categoryName.toLowerCase()) {
    case 'bil & båt':
      return {
        description: 'Utstyr for å ta vare på det du elsker - praktisk utstyr til vedlikehold',
        color: undefined,
        size: 'Small',
      }
    case 'bygg & verktøy':
      return {
        description: 'Borhammer, dykkertpistol og krysslaser - og andre verktøy til både små og store prosjekter',
        color: '#E6B1AC',
        size: 'Large',
      }
    case 'flytteutstyr':
      return {
        description: 'Trappetralle, bærestropper, skaphenger - og annet smart utstyr for flytting',
        color: undefined,
        size: 'Small',
      }
    case 'hage & utemiljø':
      return {
        description: 'Kvistkvern, plenlufter, jordbor - og annet som gjør det lettere å vedlikeholde hagen',
        color: '#E6CB86',
        size: 'Small',
      }
    case 'hengere & transport':
      return {
        description: 'Skaphenger, grindhenger, bilhenger - og alt du trenger for frakt og flyttelass',
        color: '#E6CB86',
        size: 'Large',
      }
    case 'lift & stillas':
      return {
        description: 'Tilhengerlift og fasadestillas - for trygg jobbing i høyden',
        color: undefined,
        size: 'Small',
      }
    case 'male & fikse':
      return {
        description: 'Gipsheis, giraffsliper, tapetfjerner - og annet utstyr som gjør oppussingen lettere',
        color: theme.token.var('colors.green.a150'),
        size: 'Large',
      }
    case 'rydde & rengjøre':
      return {
        description: 'Tepperenser, dampvasker og høytrykkspyler - og annet utstyr som gjør jobben enkel',
        color: '#E6B1AC',
        size: 'Large',
      }
    default:
      return {
        description: '',
        color: undefined,
        size: 'Small',
      }
  }
}

const HeroCategoryBoxProd = ({ categories }: Props): JSX.Element => {
  const pathname = usePathname()
  const router = useRouter()
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined)

  const handleCategoryBoxClick = async (categoryId: number, url: string, e: any) => {
    e.preventDefault()
    const category = categories.find((s) => s.id === categoryId)
    setSelectedCategory(categoryId)
    await trackClickCategoryOnHomepage(
      getValidTranslation('', category?.nameTranslations ?? [])?.translation ?? '',
      pathname ?? '',
    )

    router.push(url)
  }

  const organizeCategories = () => {
    // The pairs will be rendered as columns

    const mapped: UICategory[] = categories.map((category) => {
      const name = getCategoryName(category)
      const { description, color, size } = getProductCategoryDescriptionAndColourBasedOnCategoryName(name)
      return {
        id: category.id,
        name,
        imageUrl: category.imageUrl,
        size,
        description,
        backgroundColor: color,
      }
    })

    const grouped: UICategory[][] = []
    for (let i = 0; i < mapped.length; i += 2) {
      grouped.push(mapped.slice(i, i + 2))
    }

    return grouped
  }

  const categoryPairs = useMemo(organizeCategories, [categories])

  return (
    <Flex wrap="wrap" gap="16px" justify="start" width="100%" marginTop={10}>
      {categoryPairs.map((pair, index) => (
        <Flex
          key={index}
          direction="column"
          gap="16px"
          flex="1 1 200px"
          minW={{
            base: 'calc(50% - 8px)',
            // sm: 'calc(50% - 8px)',
            // md: 'calc(33.33% - 10.66px)', // 3 columns with 8 categories wraps ugly
            lg: 'calc(25% - 12px)',
          }}
          maxW="150px"
        >
          {pair.map((cat) => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              categories={categories}
              selectedCategory={selectedCategory}
              handleCategoryBoxClick={handleCategoryBoxClick}
            />
          ))}
        </Flex>
      ))}
    </Flex>
  )
}

export default HeroCategoryBoxProd
